import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import DeleteIcon from "@mui/icons-material/DeleteForeverRounded";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { RAIL_LABEL } from "../../../constants/configuration.const";
import DragHandleIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/Lock";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import { Button, IconButton, TextField, Tooltip } from "@mui/material";
import BannerForm from "./BannerForm";
import GenreForm from "./GenreForm";
import { Check, Close } from "@mui/icons-material";
import { GENRE_RAIL_TITLE } from "../LandingPage/constants";

const GenreContentCurationCard = forwardRef(
    (
        {
            dragHandleProps,
            onClickDelete,
            onClickEdit,
            onClickAdd,
            data,
            className,
            hideAddButton,
            handleGenres,
            genreMasterList,
            handleGenreTitle,
            allowEdit,
            handleRail,
        }: any,
        ref: any
    ) => {
        // hooks
        const [showGenreList, setShowGenreList] = useState(false);
        const { enqueueSnackbar } = useSnackbar();
        const genreListRef = useRef<any>(null);
        const [genreInfoDraft, setGenreInfoDraft] = useState<any>(null);
        const [errorMessage, setErrorMessage] = useState("");
        const [enableTitleEdit, setEnableTitleEdit] = useState(false);
        const [genreTitle, setGenreTitle] = useState(data?.genreTitle);
        const [formChangeKey, setFormChange] = useState<any>(null);
        const [formValid, setFormValid] = useState<any>(null);

        useImperativeHandle(
            ref,
            () => ({
                onOpenForm,
                isValid,
                handleSubmit,
                getFormValue,
            }),
            [showGenreList, formChangeKey, formValid]
        );

        const onOpenForm = () => {
            setShowGenreList(true);
            setTimeout(() => {
                genreListRef.current.validateForm();
            }, 100);
        };

        const isValid = () => {
            if (showGenreList) {
                return genreListRef.current.isValidForm();
            } else {
                return formValid?.isValid == null ? null : formValid?.isValid;
            }
        };

        const handleSubmit = () => {
            setShowGenreList(true);
            setTimeout(() => {
                saveGenres();
            }, 100);
        };
        const getFormValue = () =>
            genreListRef?.current?.getFormValues() || formValid.values;

        const onOpenGenre = () => {
            setShowGenreList(true);
        };

        const onCloseGenre = () => {
            const { isValidForm, getFormValues } = genreListRef.current;
            setFormValid({ isValid: isValidForm(), values: getFormValues() });
            setShowGenreList(false);
            handleRail(getFormValues());
        };

        useEffect(() => {
            if (data) {
                setGenreTitle(data?.railTitle);
            }
        }, [data]);

        const saveGenres = () => {
            const validForm = genreListRef.current.isValidForm();
            if (validForm) {
                enqueueSnackbar("Genre Added", {
                    variant: "info",
                    autoHideDuration: 2000,
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "center",
                    },
                });
                setGenreInfoDraft(null);
                setErrorMessage("");
                setShowGenreList(false);
                genreListRef.current.handleSubmitFormik();
            } else {
                setErrorMessage("Please fill all the fields");
            }
        };

        return (
            <div
                className={
                    "curation-list-card-container d-flex j-center curation-card"
                }
            >
                <div {...dragHandleProps} className="d-flex float-center">
                    <DragHandleIcon className="drag-handle" />
                </div>
                <div className="curation-list-card hero-carousal-card-height">
                    {/* Row One */}
                    <div {...dragHandleProps} className="d-flex j-space">
                        <span className="curation-title">
                            #{RAIL_LABEL[data.template]}
                        </span>
                        <div className="f-bold">
                            {enableTitleEdit ? (
                                <>
                                    <TextField
                                        label="Genre Title"
                                        id="outlined-size-small"
                                        value={genreTitle}
                                        size="small"
                                        onChange={(e: any) => {
                                            const title = e.target.value || "";
                                            setGenreTitle(title);
                                        }}
                                        error={
                                            genreTitle.length > GENRE_RAIL_TITLE
                                        }
                                        helperText={
                                            genreTitle.length >
                                                GENRE_RAIL_TITLE &&
                                            `Genre title cannot exceed ${GENRE_RAIL_TITLE} characters`
                                        }
                                    />
                                    <Tooltip title="Save genre">
                                        <IconButton
                                            disabled={
                                                genreTitle.length == 0 ||
                                                genreTitle.length >
                                                    GENRE_RAIL_TITLE
                                            }
                                        >
                                            <Check
                                                onClick={() => {
                                                    setEnableTitleEdit(false);
                                                    handleGenreTitle(
                                                        genreTitle
                                                    );
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                        <IconButton>
                                            <Close
                                                onClick={() => {
                                                    setGenreTitle(
                                                        data?.railTitle
                                                    );
                                                    setEnableTitleEdit(false);
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                <span
                                    onClick={() =>
                                        allowEdit && setEnableTitleEdit(true)
                                    }
                                >
                                    {" "}
                                    {genreTitle}
                                </span>
                            )}
                            <div></div>
                        </div>
                        <div>
                            <Tooltip title="Delete">
                                <span
                                    className="cursor-pointer delete-circle-style"
                                    onClick={onClickDelete}
                                >
                                    {allowEdit && <DeleteIcon />}
                                </span>
                            </Tooltip>
                        </div>
                    </div>

                    {/* row content */}
                    {showGenreList && (
                        <GenreForm
                            allowEdit={allowEdit}
                            draft={genreInfoDraft}
                            genres={data?.genres}
                            ref={genreListRef}
                            genreMasterList={genreMasterList}
                            handleGenres={handleGenres}
                        />
                    )}

                    <div {...dragHandleProps} className="mt-3 d-flex j-space">
                        {!showGenreList && (
                            <>
                                <span
                                    onClick={onOpenGenre}
                                    className="curation-edit cursor-pointer"
                                >
                                    Edit Rail
                                </span>
                                <KeyboardArrowDownIcon
                                    className="cursor-pointer"
                                    onClick={onOpenGenre}
                                />
                            </>
                        )}
                    </div>

                    {showGenreList && (
                        <div
                            {...dragHandleProps}
                            className="d-flex flex-column"
                        >
                            <div className="d-flex j-end">
                                {allowEdit && (
                                    <Button
                                        onClick={saveGenres}
                                        className="max-content-width float-center align-right mr-2"
                                        variant="contained"
                                        type="button"
                                    >
                                        Save Genre
                                    </Button>
                                )}
                                <KeyboardArrowUpIcon
                                    className="align-right cursor-pointer"
                                    onClick={onCloseGenre}
                                />
                            </div>
                            {errorMessage && (
                                <div className="text-danger d-flex j-end publish-error-text">
                                    {errorMessage}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div
                    {...dragHandleProps}
                    className="curation-list-card-container-span"
                >
                    {!hideAddButton && allowEdit && (
                        <Tooltip title="Add New Rail">
                            <span
                                className="cursor-pointer add-circle-style"
                                onClick={onClickAdd}
                            >
                                <AddCircleOutlineIcon />
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>
        );
    }
);

export default GenreContentCurationCard;
